import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, CheckIcon } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  adminEditUserSchema,
  type AdminEditUserFormData,
} from "@/modules/hub/features/UserManagement/schemas/userSchemas";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import TaggedPermissionList, {
  TaggedPermissionItem,
} from "@/components/TaggedPermissionList/TaggedPermissionList";
import PermissionOverridesEditor from "@/components/PermissionOverridesEditor/PermissionOverridesEditor";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { fetchAdminUserFormDetails } from "../../api/fetchAdminUserFormDetails";
import {
  AdminUserFormDetails,
  PermissionOverride,
} from "../../api/fetchAdminUserFormDetails/types";
import { editAdminUser } from "../../api/editAdminUser";
import {
  CompanyPermissionOverride,
  fetchPermissionsForCompany,
  PermissionForDropdown,
  StagePermissionForDisplay,
} from "../../api/fetchPermissionsForCompany";
import { addPermissionOverride } from "../../api/addPermissionOverride";
import { removePermissionOverride } from "../../api/removePermissionOverride";

// Extended entity type for companies
interface CompanyEntity {
  id: string;
  name: string;
  isCertainPath: boolean;
}

const sourceTags = {
  role: "granted by user business role",
  stage: "granted by company stage",
  company: "granted by company override",
  user: "granted by user override",
  companyRevoke: "revoked by company override",
};

const AdminEditUser: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [saveLoading, setSaveLoading] = useState(false);
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );

  const [formDetails, setFormDetails] = useState<AdminUserFormDetails | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyEntity | null>(
    null,
  );

  // Permission override state
  const [permissionOverrides, setPermissionOverrides] = useState<
    PermissionOverride[]
  >([]);
  const [availablePermissions, setAvailablePermissions] = useState<
    PermissionForDropdown[]
  >([]);
  const [stagePermissions, setStagePermissions] = useState<
    StagePermissionForDisplay[]
  >([]);
  const [companyPermissionOverrides, setCompanyPermissionOverrides] = useState<
    CompanyPermissionOverride[]
  >([]);
  const [selectedPermissionId, setSelectedPermissionId] = useState<string>("");
  const [selectedOverrideType, setSelectedOverrideType] = useState<
    "grant" | "revoke"
  >("grant");
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);
  // Inherited-from-company permissions are read-only here; collapsed by default.
  const [showInherited, setShowInherited] = useState(false);

  // Check if user is editing their own account
  const isEditingSelf = userAppSettings?.employeeUuid === uuid;

  const form = useForm<AdminEditUserFormData>({
    resolver: zodResolver(adminEditUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessRoleId: undefined,
      companyUuid: "",
    },
    mode: "onChange",
  });

  const { control, setValue, handleSubmit, formState, watch } = form;
  const { isSubmitting, isValid } = formState;
  const selectedRoleId = watch("businessRoleId");

  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!uuid) return;
      try {
        setLoading(true);
        const response = await fetchAdminUserFormDetails(uuid);
        setFormDetails(response.data);

        // Populate form with existing values
        if (response.data.firstName) {
          setValue("firstName", response.data.firstName);
        }
        if (response.data.lastName) {
          setValue("lastName", response.data.lastName);
        }
        if (response.data.email) {
          setValue("email", response.data.email);
        }
        if (response.data.employeeBusinessRoleId) {
          setValue("businessRoleId", response.data.employeeBusinessRoleId);
        }
        if (response.data.companyUuid && response.data.companyName !== null) {
          setValue("companyUuid", response.data.companyUuid);
          // Initialize selectedCompany from the response
          setSelectedCompany({
            id: response.data.companyUuid,
            name: response.data.companyName,
            isCertainPath: response.data.companyIsCertainPath || false,
          });
        }

        // Load existing permission overrides
        if (response.data.employeePermissionOverrides) {
          setPermissionOverrides(response.data.employeePermissionOverrides);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load user details",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchFormDetails();
  }, [uuid, setValue]);

  // Fetch available permissions when company changes
  useEffect(() => {
    const loadPermissions = async () => {
      if (!selectedCompany) {
        setAvailablePermissions([]);
        setStagePermissions([]);
        setCompanyPermissionOverrides([]);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await fetchPermissionsForCompany(selectedCompany.id);
        setAvailablePermissions(response.data.permissions);
        setStagePermissions(response.data.stagePermissions);
        setCompanyPermissionOverrides(response.data.companyPermissionOverrides);
      } catch (err) {
        console.error("Failed to load permissions:", err);
        setAvailablePermissions([]);
        setStagePermissions([]);
        setCompanyPermissionOverrides([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadPermissions();
  }, [selectedCompany]);

  // Handle adding a permission override
  const handleAddOverride = async () => {
    if (!uuid || !selectedPermissionId) return;

    try {
      setOverrideLoading(true);
      const response = await addPermissionOverride(uuid, {
        permissionId: parseInt(selectedPermissionId, 10),
        overrideType: selectedOverrideType,
      });

      // Update local state - replace if exists, add if new
      setPermissionOverrides((prev) => {
        const existing = prev.findIndex(
          (o) => o.permissionId === response.data.override.permissionId,
        );
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = response.data.override;
          return updated;
        }
        return [...prev, response.data.override];
      });

      // Reset selection
      setSelectedPermissionId("");
      setSelectedOverrideType("grant");

      showNotification("Permission override added", "", "success");
    } catch {
      // Error handled by axios interceptor
    } finally {
      setOverrideLoading(false);
    }
  };

  // Handle removing a permission override
  const handleRemoveOverride = async (overrideId: number) => {
    if (!uuid) return;

    try {
      setOverrideLoading(true);
      await removePermissionOverride(uuid, overrideId);

      // Update local state
      setPermissionOverrides((prev) => prev.filter((o) => o.id !== overrideId));

      showNotification("Permission override removed", "", "success");
    } catch {
      // Error handled by axios interceptor
    } finally {
      setOverrideLoading(false);
    }
  };

  const onSubmit = async (values: AdminEditUserFormData) => {
    if (uuid) {
      try {
        setSaveLoading(true);
        await editAdminUser(uuid, {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          businessRoleId: values.businessRoleId,
          companyUuid: values.companyUuid,
        });
        showNotification(
          "Successfully updated user!",
          "The user information has been updated.",
          "success",
        );
        navigate(`/admin/users/${uuid}/edit`);
      } catch {
        // Error is handled by axios interceptor
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const availableRoles = formDetails?.availableRoles || [];

  // Filter roles based on selected company
  // If CertainPath company → only show CP-only roles
  // If non-CP company → only show non-CP-only roles
  const filteredRoles = useMemo(() => {
    if (!selectedCompany) return [];
    if (selectedCompany.isCertainPath) {
      return availableRoles.filter((role) => role.isCertainPathOnly);
    }
    return availableRoles.filter((role) => !role.isCertainPathOnly);
  }, [availableRoles, selectedCompany]);

  const selectedRole =
    filteredRoles.find((r) => r.id === selectedRoleId) || null;

  // Effective permissions, mirroring the backend merge precedence:
  // (role UNION stage) + company grants - company revokes
  // + user grants - user revokes
  const effectivePermissions = useMemo(() => {
    const merged = new Map<number, TaggedPermissionItem>();

    for (const permission of selectedRole?.permissions ?? []) {
      merged.set(permission.id, {
        id: permission.id,
        label: permission.label,
        tag: sourceTags.role,
      });
    }

    for (const permission of stagePermissions) {
      if (!merged.has(permission.id)) {
        merged.set(permission.id, {
          id: permission.id,
          label: permission.label,
          tag: sourceTags.stage,
        });
      }
    }

    for (const override of companyPermissionOverrides) {
      if (override.overrideType === "revoke") {
        merged.delete(override.permissionId);
      } else if (!merged.has(override.permissionId)) {
        merged.set(override.permissionId, {
          id: override.permissionId,
          label: override.permissionLabel,
          tag: sourceTags.company,
        });
      }
    }

    for (const override of permissionOverrides) {
      if (override.overrideType === "revoke") {
        merged.delete(override.permissionId);
      } else if (!merged.has(override.permissionId)) {
        merged.set(override.permissionId, {
          id: override.permissionId,
          label: override.permissionLabel,
          tag: sourceTags.user,
        });
      }
    }

    return Array.from(merged.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [
    selectedRole,
    stagePermissions,
    companyPermissionOverrides,
    permissionOverrides,
  ]);

  // What the selected company passes down to its members: stage grants plus
  // company grant/revoke overrides, one row per permission, sorted by label.
  const inheritedCompanyItems = useMemo(() => {
    const merged = new Map<number, TaggedPermissionItem>();

    for (const permission of stagePermissions) {
      merged.set(permission.id, {
        id: permission.id,
        label: permission.label,
        tag: sourceTags.stage,
      });
    }

    for (const override of companyPermissionOverrides) {
      if (override.overrideType === "revoke") {
        merged.set(override.permissionId, {
          id: override.permissionId,
          label: override.permissionLabel,
          tag: sourceTags.companyRevoke,
        });
      } else if (!merged.has(override.permissionId)) {
        merged.set(override.permissionId, {
          id: override.permissionId,
          label: override.permissionLabel,
          tag: sourceTags.company,
        });
      }
    }

    return Array.from(merged.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [stagePermissions, companyPermissionOverrides]);

  // Clear role selection if it's no longer valid for the selected company
  useEffect(() => {
    if (selectedRoleId && !filteredRoles.find((r) => r.id === selectedRoleId)) {
      setValue("businessRoleId", undefined as unknown as number);
    }
  }, [filteredRoles, selectedRoleId, setValue]);

  // Fetch companies for EntitySingleSelect - use 100 per page like CompanySwitcherModal
  const fetchCompaniesForSelect = useCallback(
    async ({
      searchTerm,
      page,
    }: {
      searchTerm?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const result = await fetchCompanies({
        searchTerm,
        page,
        pageSize: 100,
        sortBy: "companyName",
        sortOrder: "ASC",
      });
      const { companies } = result.data;
      const totalCount = result.meta?.totalCount || companies.length;
      return {
        data: companies.map((c) => ({
          id: c.uuid,
          name: c.companyName,
          isCertainPath: c.isCertainPath || false,
        })),
        totalCount,
      };
    },
    [],
  );

  // Handle company selection
  const handleCompanyChange = useCallback(
    (company: CompanyEntity | null) => {
      setSelectedCompany(company);
      setValue("companyUuid", company?.id || "", { shouldValidate: true });
    },
    [setValue],
  );

  // Custom row renderer to show CP badge
  const renderCompanyRow = useCallback(
    (
      company: CompanyEntity,
      isSelected: boolean,
      toggle: (ent: CompanyEntity) => void,
    ) => (
      <button
        className={`w-full px-3 py-2 text-left transition-colors ${
          isSelected
            ? "bg-gray-100 dark:bg-gray-800"
            : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
        }`}
        onClick={() => toggle(company)}
        type="button"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
              {company.name}
            </span>
            {company.isCertainPath && (
              <span className="text-[10px] bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 px-1.5 py-0.5 rounded-full flex-shrink-0">
                CP
              </span>
            )}
          </div>
          {isSelected && (
            <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
          )}
        </div>
      </button>
    ),
    [],
  );

  if (!uuid) {
    return null;
  }

  return (
    <MainPageWrapper
      error={error}
      loading={loading || saveLoading}
      title="Edit User"
    >
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter email address"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="companyUuid"
            render={() => (
              <FormItem>
                <FormLabel>Company *</FormLabel>
                <FormControl>
                  <EntitySingleSelect<CompanyEntity>
                    entityNamePlural="Companies"
                    entityNameSingular="Company"
                    fetchEntities={fetchCompaniesForSelect}
                    onChange={handleCompanyChange}
                    placeholder="Select a company..."
                    renderEntityRow={renderCompanyRow}
                    value={selectedCompany}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="businessRoleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Role *</FormLabel>
                <Select
                  disabled={isEditingSelf || !selectedCompany}
                  onValueChange={(value) => {
                    const parsed = parseInt(value, 10);
                    if (!isNaN(parsed)) {
                      field.onChange(parsed);
                    }
                  }}
                  value={field.value ? field.value.toString() : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          selectedCompany
                            ? "Select a role..."
                            : "Select a company first..."
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {filteredRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.label}
                        {role.isCertainPathOnly && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                            CP
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isEditingSelf && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800/50">
                    <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      You cannot change your own role. To prevent locking
                      yourself out, ask another administrator to update your
                      role.
                    </p>
                  </div>
                )}
                {!isEditingSelf && selectedRole?.description && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
                    <Info className="h-4 w-4 text-slate-500 dark:text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {selectedRole.description}
                    </p>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-4 pt-4">
            <Button
              onClick={() => navigate("/admin/users")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={!isValid || isSubmitting || saveLoading}
              type="submit"
            >
              {isSubmitting || saveLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>

          {/* Permissions */}
          {selectedCompany && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Permissions</CardTitle>
                <CardDescription>
                  Permissions from the user&apos;s business role and company
                  stage, plus user-level grant/revoke overrides.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-medium text-sm text-gray-900 dark:text-white">
                    This user has access to:
                  </h3>
                  {effectivePermissions.length > 0 ? (
                    <div className="mt-2">
                      <TaggedPermissionList items={effectivePermissions} />
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground italic">
                      This user&apos;s role and company grant no permissions.
                    </p>
                  )}
                </div>

                {inheritedCompanyItems.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm text-gray-900 dark:text-white">
                        Inherited from company
                      </h3>
                      <button
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={() => setShowInherited((prev) => !prev)}
                        type="button"
                      >
                        {showInherited ? "Hide" : "Show"}
                      </button>
                    </div>
                    {showInherited ? (
                      <div className="mt-2 space-y-2">
                        <TaggedPermissionList items={inheritedCompanyItems} />
                        <Link
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          to={`/admin/companies/${selectedCompany.id}/edit`}
                        >
                          Manage company permissions
                        </Link>
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground italic">
                        Read-only. Managed on the company page.
                      </p>
                    )}
                  </div>
                )}

                <PermissionOverridesEditor
                  availablePermissions={availablePermissions}
                  description="Grant or revoke specific permissions for this user beyond their role and company defaults."
                  onAddOverride={handleAddOverride}
                  onRemoveOverride={handleRemoveOverride}
                  onSelectedOverrideTypeChange={setSelectedOverrideType}
                  onSelectedPermissionIdChange={setSelectedPermissionId}
                  overrideLoading={overrideLoading}
                  overrides={permissionOverrides}
                  permissionsLoading={permissionsLoading}
                  selectedOverrideType={selectedOverrideType}
                  selectedPermissionId={selectedPermissionId}
                />
              </CardContent>
            </Card>
          )}
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default AdminEditUser;
